// Project backups: one .zip holding a JSON of the project (settings, brand kit, renders, levels,
// hotspots) and every file in its draft folder. Importing gives every row a new id, so a backup can
// go into a new project or replace an existing one, even while the original still exists.
import { select, insert, update, remove } from './supa';
import { draftUrl, listFiles, uploadFile, deleteFile, clearProjectContent, syncPublished, type Project } from './data';

const FORMAT = 'archviewvr-backup';
const JSON_NAME = 'archviewvr-backup.json';
/** Project columns a backup carries. Name, link, billing, password and limits stay with the project. */
const PROJECT_KEYS = [
  'name', 'client_name', 'settings', 'brand', 'lead_enabled', 'lead_required', 'lead_email',
  'start_pano_id', 'start_heading', 'start_pitch', 'model_key', 'model_format', 'model_scale', 'model_z_up', 'storage_bytes',
];
const RESTORE_KEYS = PROJECT_KEYS.filter((k) => k !== 'name' && k !== 'client_name' && k !== 'start_pano_id');

type Row = Record<string, unknown> & { id: string };
export interface Backup {
  format: string;
  version: number;
  exportedAt: string;
  projectId: string;
  project: Record<string, unknown>;
  levels: Row[];
  panoramas: Row[];
  hotspots: Row[];
  files: Array<{ key: string; size: number }>;
}
export type Progress = (text: string, fraction: number) => void;

// ------------------------------------------------------------------ zip (stored, no compression: renders are JPEGs already)

const CRC = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; }
  return t;
})();
function crc32(b: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < b.length; i++) c = CRC[(c ^ b[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

class ZipWriter {
  private parts: BlobPart[] = [];
  private central: BlobPart[] = [];
  private offset = 0;
  private count = 0;
  private time: number;
  private date: number;
  constructor() {
    const d = new Date();
    this.time = (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2);
    this.date = ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
  }
  async add(name: string, data: Blob): Promise<void> {
    const crc = crc32(new Uint8Array(await data.arrayBuffer()));
    const nm = new TextEncoder().encode(name);
    const size = data.size;
    if (this.offset + 30 + nm.length + size > 0xfffffff0) throw new Error('This project is too big for one backup file (over 4 GB). Contact us and we’ll export it for you.');
    const h = new DataView(new ArrayBuffer(30));
    h.setUint32(0, 0x04034b50, true); h.setUint16(4, 20, true); h.setUint16(6, 0x0800, true); h.setUint16(8, 0, true);
    h.setUint16(10, this.time, true); h.setUint16(12, this.date, true); h.setUint32(14, crc, true);
    h.setUint32(18, size, true); h.setUint32(22, size, true); h.setUint16(26, nm.length, true); h.setUint16(28, 0, true);
    const c = new DataView(new ArrayBuffer(46));
    c.setUint32(0, 0x02014b50, true); c.setUint16(4, 20, true); c.setUint16(6, 20, true); c.setUint16(8, 0x0800, true); c.setUint16(10, 0, true);
    c.setUint16(12, this.time, true); c.setUint16(14, this.date, true); c.setUint32(16, crc, true);
    c.setUint32(20, size, true); c.setUint32(24, size, true); c.setUint16(28, nm.length, true);
    c.setUint32(42, this.offset, true);
    this.parts.push(h.buffer, nm, data);
    this.central.push(c.buffer, nm);
    this.offset += 30 + nm.length + size;
    this.count++;
  }
  finish(): Blob {
    const cd = new Blob(this.central);
    const e = new DataView(new ArrayBuffer(22));
    e.setUint32(0, 0x06054b50, true); e.setUint16(8, this.count, true); e.setUint16(10, this.count, true);
    e.setUint32(12, cd.size, true); e.setUint32(16, this.offset, true);
    return new Blob([...this.parts, cd, e.buffer], { type: 'application/zip' });
  }
}

type Entries = Map<string, () => Promise<Blob>>;
async function readZip(file: Blob): Promise<Entries> {
  const tailLen = Math.min(file.size, 65557);
  const tail = new DataView(await file.slice(file.size - tailLen).arrayBuffer());
  let e = -1;
  for (let i = tailLen - 22; i >= 0; i--) if (tail.getUint32(i, true) === 0x06054b50) { e = i; break; }
  if (e < 0) throw new Error('That file isn’t an ArchviewVR backup.');
  const n = tail.getUint16(e + 10, true), cdSize = tail.getUint32(e + 12, true), cdOff = tail.getUint32(e + 16, true);
  const cd = new DataView(await file.slice(cdOff, cdOff + cdSize).arrayBuffer());
  const dec = new TextDecoder();
  const out: Entries = new Map();
  let p = 0;
  for (let i = 0; i < n && p + 46 <= cd.byteLength; i++) {
    if (cd.getUint32(p, true) !== 0x02014b50) break;
    const method = cd.getUint16(p + 10, true), csize = cd.getUint32(p + 20, true);
    const nlen = cd.getUint16(p + 28, true), xlen = cd.getUint16(p + 30, true), clen = cd.getUint16(p + 32, true);
    const off = cd.getUint32(p + 42, true);
    const name = dec.decode(new Uint8Array(cd.buffer, cd.byteOffset + p + 46, nlen));
    p += 46 + nlen + xlen + clen;
    if (name.endsWith('/')) continue;
    out.set(name, async () => {
      const lh = new DataView(await file.slice(off, off + 30).arrayBuffer());
      const start = off + 30 + lh.getUint16(26, true) + lh.getUint16(28, true);
      const raw = file.slice(start, start + csize);
      if (method === 0) return raw;
      if (method === 8) return new Response(raw.stream().pipeThrough(new DecompressionStream('deflate-raw'))).blob();
      throw new Error('The backup was re-zipped in a way that can’t be read. Use the file as it was exported.');
    });
  }
  return out;
}

// ------------------------------------------------------------------ export

const fileSafe = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'project';

/** Builds the backup and downloads it. */
export async function exportProject(projectId: string, progress: Progress): Promise<void> {
  progress('Reading the project', 0);
  const [p] = await select<Row>('projects', `id=eq.${projectId}&select=*`);
  if (!p) throw new Error('Project not found');
  const [levels, panoramas, hotspots, files] = await Promise.all([
    select<Row>('levels', `project_id=eq.${projectId}&select=*&order=sort.asc`),
    select<Row>('panoramas', `project_id=eq.${projectId}&select=*&order=sort.asc`),
    select<Row>('hotspots', `project_id=eq.${projectId}&select=*&order=created_at.asc`),
    listFiles(projectId),
  ]);
  const project: Record<string, unknown> = {};
  for (const k of PROJECT_KEYS) if (k in p) project[k] = p[k];
  const backup: Backup = { format: FORMAT, version: 1, exportedAt: new Date().toISOString(), projectId, project, levels, panoramas, hotspots, files };
  const zip = new ZipWriter();
  await zip.add(JSON_NAME, new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' }));
  const total = files.reduce((n, f) => n + f.size, 0) || 1;
  let done = 0;
  for (const f of files) {
    progress(`Adding ${f.key}`, done / total);
    const res = await fetch(draftUrl(projectId, f.key), { cache: 'no-store' });
    if (!res.ok) throw new Error(`Couldn’t read ${f.key}. Try again.`);
    await zip.add(`files/${f.key}`, await res.blob());
    done += f.size;
  }
  progress('Saving the file', 1);
  const blob = zip.finish();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${fileSafe(String(p.name ?? 'project'))}-backup-${new Date().toISOString().slice(0, 10)}.zip`;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 60_000);
}

// ------------------------------------------------------------------ import

export interface OpenBackup { backup: Backup; entries: Entries; prefix: string; size: number }

/** Opens a backup file and reads its JSON. */
export async function openBackup(file: File): Promise<OpenBackup> {
  const entries = await readZip(file);
  // a backup unzipped and zipped again may sit inside a folder
  const jsonName = [...entries.keys()].find((k) => k === JSON_NAME || k.endsWith(`/${JSON_NAME}`));
  if (!jsonName) throw new Error('That file isn’t an ArchviewVR backup.');
  const backup = JSON.parse(await (await entries.get(jsonName)!()).text()) as Backup;
  if (backup.format !== FORMAT || !Array.isArray(backup.panoramas)) throw new Error('That file isn’t an ArchviewVR backup.');
  return { backup, entries, prefix: jsonName.slice(0, -JSON_NAME.length), size: backup.files.reduce((n, f) => n + f.size, 0) };
}

/**
 * Replaces a project's renders, model, levels, hotspots, brand kit and settings with a backup's.
 * The project's name, share link, billing, password and limits stay as they are.
 */
export async function restoreProject(target: Project, src: OpenBackup, progress: Progress): Promise<void> {
  const b = src.backup;
  if (src.size > target.storage_limit) throw new Error('This backup is bigger than the project’s storage limit. Contact us for more space.');

  // new ids for every row; file names and settings that mention an old id get the new one
  const ids = new Map<string, string>([[b.projectId.toLowerCase(), target.id]]);
  for (const r of [...b.levels, ...b.panoramas, ...b.hotspots]) ids.set(String(r.id).toLowerCase(), crypto.randomUUID());
  const re = new RegExp([...ids.keys()].map((k) => k.replace(/[-]/g, '\\-')).join('|'), 'gi');
  const remap = (s: string) => s.replace(re, (m) => ids.get(m.toLowerCase()) ?? m);
  const remapJson = <T>(v: T): T => JSON.parse(remap(JSON.stringify(v))) as T;

  // 1 · files
  const before = await listFiles(target.id);
  const written = new Set<string>();
  const total = src.size || 1;
  let done = 0;
  for (const f of b.files) {
    const get = src.entries.get(`${src.prefix}files/${f.key}`);
    if (!get) continue;
    const key = remap(f.key);
    progress(`Uploading ${key}`, done / total);
    let blob = await get();
    if (key === 'manifest.json') {
      const m = JSON.parse(remap(await blob.text()));
      m.id = target.id; m.name = target.name; m.state = target.state === 'published' ? 'published' : 'trial';
      blob = new Blob([JSON.stringify(m)], { type: 'application/json' });
    }
    await uploadFile(target.id, key, blob);
    written.add(key);
    done += f.size;
  }

  // 2 · database: the old rows go, the backup's rows come in with new ids
  progress('Recording renders', 1);
  await clearProjectContent(target.id);
  await remove('hotspots', `project_id=eq.${target.id}`);
  const own = (rows: Row[]) => remapJson(rows).map((r) => ({ ...r, project_id: target.id }));
  if (b.levels.length) await insert('levels', own(b.levels));
  if (b.panoramas.length) await insert('panoramas', own(b.panoramas));
  if (b.hotspots.length) await insert('hotspots', own(b.hotspots));
  const values: Record<string, unknown> = {};
  for (const k of RESTORE_KEYS) if (k in b.project) values[k] = b.project[k];
  const start = b.project.start_pano_id ? ids.get(String(b.project.start_pano_id).toLowerCase()) ?? null : null;
  await update('projects', `id=eq.${target.id}`, { ...remapJson(values), start_pano_id: start });

  // 3 · files the project had that the backup doesn't
  progress('Tidying up', 1);
  for (const f of before) if (!written.has(f.key)) await deleteFile(target.id, f.key);
  if (target.state === 'published') { progress('Updating the live tour', 1); await syncPublished(target.id); }
}
